import React from 'react';
import { View, StyleSheet, ScrollView, Platform } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { supabase } from '@cubemeet/core/services/supabase';
import { getCurrentUser } from '@cubemeet/core/store/slices/authSlice';
import { AppDispatch } from '../store';
import { SettingsGroup, SettingsItem, SettingsDivider, SettingsHeader } from '@cubemeet/ui';
import { useSettingsTranslation } from '../modules/settings/i18n';

const SettingsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t: settingsT } = useSettingsTranslation();

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch {}
    try {
      await dispatch(getCurrentUser()).unwrap();
    } catch {}
    if (typeof window !== 'undefined') {
      window.location.replace('/');
    }
  };

  const settingsGroups = [
    {
      title: settingsT('settings.groups.privacySecurity'),
      items: [
        {
          title: settingsT('privacyScreen.privacyPolicy'),
          icon: 'policy',
          iconColor: '#4A86F7',
          route: '/settings/privacy-policy',
        },
        {
          title: settingsT('privacyScreen.termsOfService'),
          icon: 'description',
          iconColor: '#4A86F7',
          route: '/settings/terms-of-service',
        },
      ],
    },
    {
      title: settingsT('settings.groups.appSettings'),
      items: [
        {
          title: settingsT('settings.items.languageSettings.title'),
          subtitle: settingsT('settings.items.languageSettings.subtitle'),
          icon: 'language',
          iconColor: '#A8E6CF',
          route: '/settings/language',
        },
      ],
    },
  ];

  return (
    <View style={styles.container}>
      <SettingsHeader 
        title={settingsT('settings.title')} 
        showBackButton={false}
      />
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {settingsGroups.map((group) => (
          <SettingsGroup key={group.title} title={group.title}>
            {group.items.map((item, itemIndex) => (
              <React.Fragment key={item.title}>
                <SettingsItem
                  title={item.title}
                  subtitle={item.subtitle}
                  icon={item.icon}
                  iconColor={item.iconColor}
                  onPress={() => navigate(item.route)}
                />
                {itemIndex < group.items.length - 1 && <SettingsDivider />}
              </React.Fragment>
            ))}
          </SettingsGroup>
        ))}

        <SettingsGroup title="帮助与支持">
          <SettingsItem
            title="使用帮助"
            icon="description"
            iconColor="#D4A5A5"
            onPress={() => {}}
          />
          <SettingsDivider />
          <SettingsItem
            title="联络我们"
            subtitle="support@cubemeet.app"
            icon="mail"
            iconColor="#4A86F7"
            onPress={() => {
              if (typeof window !== 'undefined') {
                window.location.href = 'mailto:support@cubemeet.app';
              }
            }}
          />
        </SettingsGroup>
        
        <View style={styles.footerSpacer} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f6f7f8', height: '100%' },
  content: { 
    flex: 1, 
    paddingHorizontal: 15, 
    paddingTop: 15,
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
    backgroundColor: '#ffffff',
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    marginBottom: 20,
    ...Platform.select({
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }
    }),
  },
  footerSpacer: { height: 20 },
});

export default SettingsPage;
